const _ = require("lodash");
const Constants = require("../../../shared/Constants");
const URLMake = require("../../../shared/URLMake");
const PageShared = require("../../../shared/PageShared");
const {requiresFullDocumentNavigation} = require("../../../shared/ElevRoutes");
const GradeChapter = require("../../../shared/GradeChapter");
const {FLAGS} = require("../util/GlobalFlags");
const {getFeatureFlag} = require("../util/FeatureFlags");
const OpsUser = require("./OpsUser");
const {
	isUnregisteredStudent,
} = require("../../parent/util/PrimaryRegistrationUtil");

// Page shape {
//   name: string with main part of current page,
//   ... plus any other parameters needed to describe the current page.
// }
// See the URL*.js files in this folder for details and all possible shapes
// of this object.

const isInTrainer = function(page) {
	return /^trainer/.test(page.name) && !PageShared.isDevProblemPage(page);
};

const isInLibrary = function(page) {
	return /^library/.test(page.name);
};

const isInTheater = function(page) {
	return /^theater/.test(page.name);
};

const isInSchool = function(page) {
	return /^school/.test(page.name);
};

const isInLab = function(page) {
	return /^lab/.test(page.name);
};

const isInProfile = function(page) {
	return /^profile/.test(page.name);
};

const isInStudentTutorial = function(page) {
	return /^tutorial/.test(page.name);
};

const isInStudentOnboarding = function(page) {
	return page.name === "home.onboarding";
};

const isInBeastBuilder = function(page) {
	return /^beastbuilder/.test(page.name);
};

const isSignedInStudentPage = function(page) {
	return (
		isInTrainer(page) ||
		isInLibrary(page) ||
		isInTheater(page) ||
		isInLab(page) ||
		isInProfile(page) ||
		isInBeastBuilder(page) ||
		isHomeMain(page) ||
		isInStudentTutorial(page) ||
		isInStudentOnboarding(page)
	);
};

const isLibraryChapterPage = function(page) {
	return (
		page.name &&
		page.name.split(".")[0] === "library" &&
		page.name.split(".")[1] === "chapter"
	);
};

const isTheaterSetPage = function(page) {
	return (
		page.name &&
		page.name.split(".")[0] === "theater" &&
		page.name.split(".")[1] === "set"
	);
};

const isProfilePage = function(page) {
	return page.name && page.name.split(".")[0] === "profile";
};

const isStudentPage = function(page) {
	return isSignedInStudentPage(page);
};

// Pages where students are actively "learning"
const isActiveStudentPage = function(page) {
	const activePages = [
		"trainer.problem",
		"trainer.history",
		"library.chapter",
		"theater.set",
		"lab.problem",
		"tutorial.main",
	];
	return _.includes(activePages, page.name) || page.showBook;
};

const isValidTrainerChapterPage = page => {
	const {bookTestHash, gradeNumber, chapterIndex} = page;

	// Don't validate chapter info for a book test
	if (bookTestHash) {
		return true;
	}
	if (!_.isFinite(gradeNumber) || !_.isFinite(chapterIndex)) {
		return false;
	}
	if (GradeChapter.getActiveGrades().indexOf(gradeNumber) < 0) {
		return false;
	}
	const chapters = GradeChapter.getAllChaptersForGrade(gradeNumber);
	if (!_.includes(_.range(chapters.length), chapterIndex)) {
		return false;
	}
	return true;
};

const getMostRecentPageData = function(page) {
	if (PageShared.isTrainerPage(page)) {
		return _.pickBy(
			_.pick(page, [
				"gradeNumber",
				"chapterIndex",
				"blockIndex",
				"blockType",
				"pageNumber",
				"typeID",
				"labBlockID",
			]),
			prop => prop || prop === 0
		);
	}
};

// Returns same page if not on problem, else goes up one level from problem.
const leaveProblem = function(page) {
	if (page.name === "trainer.problem" || page.name === "trainer.history") {
		const newPage = URLMake.state.forChapterFromNumbers(
			page.gradeNumber,
			page.chapterIndex
		);
		newPage.demo = page.demo;
		return newPage;
	} else if (page.name === "lab.problem") {
		return URLMake.state.forLabType(page.typeID);
	} else {
		return page;
	}
};

const shouldBlockIplUser = function(page) {
	return !requiresFullDocumentNavigation(page);
};

const IplBlockedUtil = require("../util/IplBlockedUtil");

const isIplUserAppBlocked = function(user, page) {
	if (
		!user ||
		user.load !== "done" ||
		!OpsUser.isLoggedIn(user) ||
		!shouldBlockIplUser(page)
	) {
		return false;
	}

	const isStaff = OpsUser.isIplStaff(user);
	if (isStaff && IplBlockedUtil.hasStaffBypass()) {
		return false;
	}
	if (isStaff) {
		return true;
	}

	return OpsUser.isIplUser(user) && getFeatureFlag("ENABLE_IPL_USER_BLOCKING");
};

const isPageProtected = function(page) {
	const unprotectedPages = [
		"office.main",
		"office.termsAndPrivacy",
		"office.faq",
		"office.contact",
		"office.checkout",
		"office.about",
		"office.baonline",
		"office.schools",
		"office.enroll",
		"office.bookstore",
		"office.book",
		"office.apparel",
		"office.allbooks",
		"404",
		"office.mailinglist",
		"office.confirmEmail",
		"office.autoRenew",
		"office.accountSetup",
		"office.redeemSubscription",
		"office.resources",
		"office.educators",
		"office.outcomes",
		"office.playground",
		"office.chalkboard",
		"office.classtag",
		"parent.support",
		"office.cmsInfoPages",
	];
	if (page.demo)
		unprotectedPages.push(
			"home.main",
			"trainer.chapter",
			"trainer.problem",
			"trainer.history",
			"profile.main",
			"library.grade",
			"library.chapter",
			"theater.chapter",
			"theater.set"
		);
	return unprotectedPages.indexOf(page.name) < 0;
};

const isPostLoginPage = function(page) {
	return page.name === "home.postSignin";
};

const isStaticPage = function(page) {
	const staticPages = [
		"home.postSignin",
		"404",
		"office.mailinglist",
		"office.chalkboard",
		"office.classtag",
		"office.cmsInfoPages",
	];
	return staticPages.indexOf(page.name) > -1;
};

// These pages should not use the default redirect logic that takes you back to
// original page when signing in.
const isPageNotForDefaultSignin = function(page) {
	return ["office.confirmEmail"].indexOf(page.name) > -1;
};

const isAccountSetupPage = function(page) {
	return _.includes(["office.accountSetup"], page.name);
};

const getStudentRegistrationPage = function(studentID = null) {
	return URLMake.forParentStudentPrimaryRegistration(studentID);
};

const isPageForNotLoggedIn = function(page) {
	const notLoggedInPages = [];
	if (page.demo)
		notLoggedInPages.concat([
			"home.main",
			"trainer.chapter",
			"trainer.problem",
			"trainer.history",
			"profile.main",
			"library.grade",
			"library.chapter",
			"theater.chapter",
			"theater.set",
		]);
	return _.includes(notLoggedInPages, page.name);
};

const getDefaultOfficeLogoutPage = function(page) {
	if (page.name === "parent.subscribe") {
		// This is if user is on the change subscription flow pages.
		// Return to main account page.
		return URLMake.state.forAccount();
	}
};

const isPageForLogoutPrompt = function(page) {
	// Currently no pages, but this implementation is useful
	return false;
};

const getLoadedUserPage = function(currentPage, pageRequest, clock) {
	const requestPage = pageRequest && pageRequest.page;
	let loadedPage;

	if (
		requestPage &&
		requestPage.name &&
		clock.now() - pageRequest.requestedAt <
			Constants.REQUEST_PAGE_EXPIRE_MINUTES * 60 * 1000
	) {
		loadedPage = requestPage;
	} else if (!currentPage || isPostLoginPage(currentPage)) {
		// We shouldn't ever just stay on a post-login page
		loadedPage = URLMake.state.forOffice();
	} else {
		loadedPage = currentPage;
	}

	return loadedPage;
};

// Keeping this function for now even though it's always false
// We can make use of it if we're hiding a certain section of the
// trainer during development.
const checkSilentPageRestriction = function(user, page) {
	return false;
};

// Checked on page load (essentially)
const checkBadPermissions = function(user, page) {
	// If user hasn't loaded, return
	if (!user.model) return false;

	// We don't need to check for isInitialized for parents because that
	// case should never exist (as of Platform V1).
	const hasAccountAccess =
		(OpsUser.hasParentRole(user) ||
			(OpsUser.getStaffedPurchaseID(user) && OpsUser.isInitialized(user))) &&
		!OpsUser.isBetaParent(user);

	return (
		(isTeacherPage(page) &&
			(!OpsUser.hasDashboard(user) || !OpsUser.isInitialized(user))) ||
		(isProtectedParentPage(page) && !OpsUser.hasParentRole(user)) ||
		(isAccountPage(page) && !hasAccountAccess) ||
		(isPageCheckout(page) && OpsUser.isStudentOnly(user))
	);
};

// Run on each action
const checkPageRestriction = function(user, page) {
	if (_.isEmpty(user.model) || page.demo) return page;
	let validPage;

	// Checking to see if only problems can be viewed based on role
	if (PageShared.isTrainerPage(page) && !OpsUser.hasTrainerPermission(user)) {
		if (OpsUser.hasParentRole(user)) {
			validPage = URLMake.state.forHome();
		} else {
			validPage = URLMake.state.forHomeError({
				messageType: "noTrainerPermission",
				data: {userRoles: OpsUser.getUserRoles(user)},
			});
		}
	} else if (
		isCurrentlyTakingTest(user, page) &&
		!isPageForCurrentTest(user, page) &&
		!OpsUser.hasKey(user, "trainer allow unfinished test") &&
		isSignedInStudentPage(page)
	) {
		const context = OpsUser.getTestInProgressInfo(user);
		let testPage, errorMessageInfo;
		if (
			isPageForBookTestBlock(page) &&
			isCurrentlyTakingBookTest(user, page) &&
			OpsUser.bookTestAssignedNow(user)
		) {
			// Sends the user to the new test anyway.
			// Clearing the testInProgress data is cleared already by UpdateCore's
			// checkForBadTestInProgress function.
			testPage = page;
		} else if (isCurrentlyTakingBookTest(user, page)) {
			errorMessageInfo = {
				messageType: "bookTestInProgress",
				data: {
					miscBlockName: context.miscBlockName,
				},
			};
			testPage = URLMake.state.forTestBlockFromMiscBlockName(
				context.miscBlockName,
				errorMessageInfo
			);
		} else {
			const chapterID = GradeChapter.toChapterID(
				context.gradeNumber,
				context.chapterIndex
			);
			const baLevel = context.gradeNumber;
			const chapter = context.chapterIndex + 1;
			errorMessageInfo = {
				messageType: "testInProgress",
				data: {
					chapterID,
					baLevel,
					chapter,
					chapterName: context.name,
				},
			};
			testPage = URLMake.state.forTestBlockFromChapterID(
				chapterID,
				errorMessageInfo
			);
		}

		if (page.name === "home.main") {
			validPage = URLMake.state.forHomeError(errorMessageInfo);
		} else {
			// go directly to test page, do not pass go, do not collect, etc
			validPage = testPage;
		}
	} else if (
		isProfilePage(page) &&
		OpsUser.hasParentRole(user) &&
		!OpsUser.hasTrainerPermission(user)
	) {
		// For parents on profile page
		validPage = URLMake.state.forHome();
	} else if (isInBeastBuilder(page) && !OpsUser.canAccessBeastbuilder(user)) {
		// For going to beast builder without access
		validPage = URLMake.state.forProfile();
	} else if (checkSilentPageRestriction(user, page)) {
		// Should only happen for trainer pages
		validPage = URLMake.state.forHome();
	} else {
		validPage = page;
	}

	return validPage;
};

const isPageForTestBlock = function(page) {
	return !!(
		page &&
		page.name === "trainer.problem" &&
		page.blockType === "test"
	);
};

const isPageForBookTestBlock = function(page) {
	return !!(
		page &&
		page.name === "trainer.problem" &&
		page.blockType === "test" &&
		page.bookTestHash
	);
};

const isPageForCurrentTest = function(user, page) {
	const context = OpsUser.getTestInProgressInfo(user);

	const isPageForCurrentChapterTest =
		isPageForTestBlock(page) &&
		page.gradeNumber === context.gradeNumber &&
		page.chapterIndex === context.chapterIndex;
	const isPageForCurrentBookTest =
		isPageForBookTestBlock(page) && page.bookTestHash === context.miscBlockName;

	return isPageForCurrentChapterTest || isPageForCurrentBookTest;
};

const isCurrentlyTakingTest = function(user, page) {
	// TODO: remove this safeguard and implement test checking for demo when tests put on demo
	if (_.get(page, "demo") || !PageShared.isTrainerPage(page)) return false;
	const lastPlayedData = _.get(user, "model.lastPlayedData");
	return !!(lastPlayedData && lastPlayedData.testInProgress);
};

const isCurrentlyTakingChapterTest = function(user, page) {
	return (
		isCurrentlyTakingTest(user, page) &&
		!!_.get(user, "model.lastPlayedData.testInProgress.chapterID")
	);
};

const isCurrentlyTakingBookTest = function(user, page) {
	return (
		isCurrentlyTakingTest(user, page) &&
		!!_.get(user, "model.lastPlayedData.testInProgress.miscBlockName")
	);
};

//If the user is on a restricted page or requests one, decide where they should
//be redirected to based on requestedPage - the restricted page they were on
//before logging out or tried to go to needsLogin - true if we should (if
//possible) open a login modal on the page they go to
const getNonRestrictedPage = function(requestedPage) {
	let page;
	if (isOfficePage(requestedPage)) {
		page = URLMake.state.forOffice();
		return page;
	} else {
		return URLMake.state.forLogin();
	}
};

const isOfficePage = PageShared.isOfficePage;
const isHomeMain = PageShared.isHomeMain;
const isHomeOnboarding = PageShared.isHomeOnboarding;

const isHotjarPage = function(page) {
	const hotjarPages = [
		"office.main",
		"office.baonline",
		"office.enroll",
		"office.termsAndPrivacy",
		"office.resources",
		"office.contact",
		"office.faq",
		"office.about",
		"office.bookstore",
		"office.book",
		"office.allbooks",
		"office.chalkboard",
		"office.classtag",
		"office.mailinglist",
	];
	return _.includes(hotjarPages, page.name) || isSignedInStudentPage(page);
};

// Used to determine whether the current page should count as the given type
// for the top nav bar
const matchesOfficeBarType = function(page, type) {
	const typeMap = {
		books: ["office.bookstore", "office.book", "office.allbooks"],
		schools: ["office.schools"],
		online: ["office.baonline", "office.enroll"],
		teacher: ["office.teacher", "school.main"],
		parent: ["parent.main", "parent.student"],
		enroll: ["office.enroll"],
		allbooks: ["office.allbooks"],
		resources: ["office.resources", "office.contact"],
	};

	// Special cases
	if (type === "reports" || type === "accounts") {
		return page.name === "school.main" && page.space === type;
	}
	if (type === "support") {
		return (
			(page.name === "school.main" && page.space === "support") ||
			page.name === "parent.support"
		);
	}
	if (type === "resources" && page.section === "errata") {
		return false;
	}

	return _.includes(typeMap[type], page.name);
};

const isPageCheckout = function(page) {
	return page.name === "office.checkout";
};

const isPageCart = function(page) {
	return isPageCheckout(page) && page.step === "cart";
};

const isMainProfilePage = function(page) {
	return page.name === "profile.main" && page.currentTab === "profile";
};

const isOnHomeworkTab = function(page) {
	return page.name === "profile.main" && page.currentTab === "homework";
};

const isHistoryPage = function(page) {
	return page.name === "trainer.history";
};

const isParentPage = function(page) {
	return _.includes(
		["parent.main", "parent.student", "parent.support"],
		page.name
	);
};

const isProtectedParentPage = function(page) {
	return _.includes(
		["parent.main", "parent.student", "parent.subscribe"],
		page.name
	);
};

const isTeacherPage = function(page) {
	return _.includes(["school.main", "school.profile"], page.name);
};

const isReportPage = function(page) {
	return _.includes(["school.main", "parent.student"], page.name);
};

const isAccountPage = function(page) {
	return page.name === "parent.account";
};

const isMinWidthPage = function(page) {
	const pagesWithMinWidth = ["school.main"];
	return _.includes(pagesWithMinWidth, page.name);
};

//If the user is a parent, redirect them to main parent page on login
//TODO: Decide what to do if both parent and teacher.
const getDefaultRequestedPage = function(user, clock) {
	if (OpsUser.hasStudentRole(user)) {
		return {
			page: URLMake.state.forHome(),
			requestedAt: clock.now(),
		};
	} else if (OpsUser.hasParentRole(user)) {
		const students = user.model?.students;
		// Redirect to primary registration page if there are any active unregistered students
		if (students && students.some(isUnregisteredStudent)) {
			return {
				page: URLMake.state.forParentStudentPrimaryRegistration(),
				requestedAt: clock.now(),
			};
		}
		return {
			page: URLMake.state.forParent(),
			requestedAt: clock.now(),
		};
	} else if (
		OpsUser.getStaffedPurchaseID(user) &&
		OpsUser.isStaffActive(user) &&
		OpsUser.hasRole(user, "teacher")
	) {
		const staffRoles = _.get(user, "model.staffRoles", {});
		if (staffRoles.adminPurchase) {
			return {
				page: URLMake.state.forSchool("accounts"),
				requestedAt: clock.now(),
			};
		} else if (
			staffRoles.teachClassIDs.length ||
			staffRoles.adminSchoolIDs.length
		) {
			return {
				page: URLMake.state.forSchool("reports"),
				requestedAt: clock.now(),
			};
		} else if (staffRoles.teachClassIDs.length === 0) {
			// Teacher has no classes, but should still be redirected to school.main
			return {
				page: URLMake.state.forSchool("reports", "classes"),
				requestedAt: clock.now(),
			};
		} else {
			return null;
		}
	} else {
		return null;
	}
};

// page is the current page. logoutPage is an optional page to go to as
// specified by a logout action.
const getLoggedOutPage = function(page, logoutPage) {
	// If we have a page we should go to on logout, go there
	// If the page they're on isn't protected or is an office page,
	// leave them there.
	// Otherwise, get the non-restricted page they should go to next.
	if (logoutPage && isPageForNotLoggedIn(logoutPage)) {
		return logoutPage;
	} else if (!isPageProtected(page)) {
		return page;
	} else if (isOfficePage(page)) {
		// If specified going to a certain page on logout, go there, else, stay
		// on that page.
		return logoutPage || getDefaultOfficeLogoutPage(page) || page;
	} else {
		return getNonRestrictedPage(page);
	}
};

const shouldShowReadAloud = function(page, {problem} = {}) {
	if (!page) return false;

	// readaloud enabled on the following
	const permittedGrades = {
		problems: [1, 2, 3],
		solutions: [1, 2],
		instructions: [1, 2],
		testOverview: [1, 2, 3],
	};

	const isProblem =
		["trainer.problem", "lab.problem"].includes(page.name) &&
		(_.isNumber(page.problemIndex) || page.problemIndex === "trophy");
	const isDevProblem = page.name === "trainer.devProblem";
	const isSolution = problem?.outcome;
	const isInstruction = page.showInstructions;
	const isTestOverview =
		page.blockType === "test" && page.problemIndex === "overview";

	let permittedGradesForPage = [];
	if (isInstruction) {
		permittedGradesForPage = permittedGrades["instructions"];
	} else if (isSolution) {
		permittedGradesForPage = permittedGrades["solutions"];
	} else if (isProblem) {
		permittedGradesForPage = permittedGrades["problems"];
	} else if (isTestOverview) {
		permittedGradesForPage = permittedGrades["testOverview"];
	}

	const isValidView =
		isProblem || isSolution || isInstruction || isTestOverview;

	const isValidGrade =
		permittedGradesForPage.includes(page.gradeNumber) ||
		FLAGS.showReadAloudEverywhereAvailable;

	return isDevProblem || (isValidView && isValidGrade);
};

const getTestOverviewReadAloudText = function() {
	return document
		.getElementById("testOverviewContent")
		?.textContent?.replace(
			/\./g,
			". "
		) /* prevent a.a from being read as "a dot a" */
		?.replace(/\!/g, "! "); /* prevent a!a from being read as "a bang a" */
};

//Tutorial pages
const isTutorialPage = function(page) {
	const tutorialPages = [
		"home.main",
		"trainer.chapter",
		"trainer.problem",
		"library.grade",
		"library.chapter",
		"profile.main",
	];
	return tutorialPages.indexOf(page.name) >= 0;
};

const bypassOnDemoPages = function(page, user) {
	if (page && !_.isEmpty(page)) {
		// logged in users should not be able to navigate demo pages while logged in
		if (_.get(page, "demo") && !OpsUser.hasParentRole(user)) {
			return true;
		}
	}
	return false;
};

const needsTrainerBundle = function(page) {
	if (isInStudentTutorial(page)) {
		return true;
	}

	return false;
};

const needsTutorialBundle = function(page) {
	if (isInStudentTutorial(page)) {
		return true;
	}
	return false;
};

const isLabReportsPageForStudent = function(page) {
	return (
		isReportPage(page) &&
		page.pageData.grade === "lab" &&
		page.pageData.studentID
	);
};

const shouldRenderTrainerBorder = function(page) {
	return !isInStudentOnboarding(page);
};

module.exports = {
	isInTrainer,
	isInLibrary,
	isInTheater,
	isInSchool,
	isInLab,
	isInStudentTutorial,
	isInStudentOnboarding,
	isInBeastBuilder,
	isStudentPage,
	isSignedInStudentPage,
	isActiveStudentPage,
	isValidTrainerChapterPage,
	getMostRecentPageData,
	leaveProblem,
	isPageProtected,
	shouldBlockIplUser,
	isIplUserAppBlocked,
	isStaticPage,
	isPageNotForDefaultSignin,
	isAccountSetupPage,
	getStudentRegistrationPage,
	isPageForNotLoggedIn,
	isPageForLogoutPrompt,
	getDefaultOfficeLogoutPage,
	getLoadedUserPage,
	checkPageRestriction,
	isPageForTestBlock,
	isPageForBookTestBlock,
	isCurrentlyTakingTest,
	isCurrentlyTakingBookTest,
	isCurrentlyTakingChapterTest,
	isPageForCurrentTest,
	getNonRestrictedPage,
	isOfficePage,
	isHomeMain,
	isHomeOnboarding,
	isProfilePage,
	isCoreTrainerPage: PageShared.isCoreTrainerPage,
	isTrainerPage: PageShared.isTrainerPage,
	isLibraryChapterPage,
	isTheaterSetPage,
	isTrainerProblemPage: PageShared.isTrainerProblemPage,
	matchesOfficeBarType,
	isPageCheckout,
	isPageCart,
	isMainProfilePage,
	isOnHomeworkTab,
	isHistoryPage,
	checkBadPermissions,
	isParentPage,
	isProtectedParentPage,
	isTeacherPage,
	isReportPage,
	isAccountPage,
	isPostLoginPage,
	getDefaultRequestedPage,
	getLoggedOutPage,
	shouldShowReadAloud,
	getTestOverviewReadAloudText,
	isTutorialPage,
	isHotjarPage,
	isMinWidthPage,
	bypassOnDemoPages,
	needsTrainerBundle,
	needsTutorialBundle,
	isLabReportsPageForStudent,
	shouldRenderTrainerBorder,
};
